import type { ActiveExecution } from "@/stores/companion-store";
import type { AgentCursor as AgentCursorData } from "@/stores/canvas-store";
import { isVisibleAgentExecutionWork } from "./agent-work-visibility";

function agentNameKey(name: string | undefined): string {
  return (name ?? "").trim().toLowerCase();
}

function executionMatchesCursor(
  execution: ActiveExecution,
  cursor: AgentCursorData,
  executions: Map<string, ActiveExecution>,
): boolean {
  if (cursor.agentId === execution.id || cursor.executionId === execution.id)
    return true;
  if (execution.spaceId && cursor.spaceId !== execution.spaceId) return false;
  const sessionId =
    cursor.sessionId ??
    executions.get(cursor.executionId ?? cursor.agentId)?.runtimeSessionId;
  if (execution.runtimeSessionId && sessionId) {
    return execution.runtimeSessionId === sessionId;
  }
  if (cursor.executionId || executions.has(cursor.agentId)) return false;
  const executionKey = agentNameKey(execution.agentName ?? execution.agentKey);
  return !!executionKey && agentNameKey(cursor.name) === executionKey;
}

/** Follow-up executions share one runtime; retained presentation cursors may
 * still belong to its preceding turn. Keep their canvas lifetime independent. */
export function getSessionDockCursors(
  cursors: AgentCursorData[],
  executions: Map<string, ActiveExecution>,
): AgentCursorData[] {
  const seen = new Set<string>();
  return cursors.filter((cursor) => {
    const sessionId =
      cursor.sessionId ??
      executions.get(cursor.executionId ?? cursor.agentId)?.runtimeSessionId;
    if (!sessionId) return true;
    const key = JSON.stringify([cursor.spaceId, sessionId]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isDockableExecution(execution: ActiveExecution): boolean {
  return (
    execution.status === "running" || execution.status === "waiting_approval"
  );
}

export function getExecutionDockCursors(
  existing: AgentCursorData[],
  executions: Map<string, ActiveExecution>,
  currentSpaceId: string | undefined,
  /** Display name for an execution that carries no agent name or key. */
  fallbackName: string,
): AgentCursorData[] {
  const synthetic: AgentCursorData[] = [];
  const now = new Date().toISOString();
  for (const execution of executions.values()) {
    if (!isDockableExecution(execution)) continue;
    if (!isVisibleAgentExecutionWork(execution)) continue;
    if (
      currentSpaceId &&
      execution.spaceId &&
      execution.spaceId !== currentSpaceId
    ) {
      continue;
    }
    if (
      existing.some((cursor) =>
        executionMatchesCursor(execution, cursor, executions),
      ) ||
      synthetic.some((cursor) =>
        executionMatchesCursor(execution, cursor, executions),
      )
    ) {
      continue;
    }
    synthetic.push({
      agentId: execution.id,
      executionId: execution.id,
      sessionId: execution.runtimeSessionId,
      spaceId: execution.spaceId ?? currentSpaceId ?? "",
      name: execution.agentName ?? execution.agentKey ?? fallbackName,
      color: "var(--color-accent)",
      targetFrameId: null,
      taskId: execution.taskId,
      lastSeen: execution.startedAt ?? now,
      origin: execution.origin ?? "chat",
    });
  }
  return synthetic;
}
